import { useEffect, useState } from "react";
import { Users, Activity, Crown, Hourglass, Timer, Ban } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const Kpi = ({ Icon, label, value, color, testId }) => (
  <div className="hud-card hud-corners p-4 sm:p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-400"><Icon size={14} style={{ color }} /> {label}</div>
    <p className="font-heading text-3xl sm:text-4xl font-bold text-white mt-2">{value}</p>
  </div>
);

const tip = { contentStyle: { background: "#0a1224", border: "1px solid rgba(34,211,238,.3)", borderRadius: 10, fontSize: 12 } };
const NAMES = { aurora: "Aurora", gpt: "GPT", claude: "Claude", gemini: "Gemini", grok: "Grok", kimi: "Kimi", deepseek: "DeepSeek", perplexity: "Perplexity" };

export const AdminDashboard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="hud-tag p-6">Caricamento telemetria…</p>;

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi Icon={Users} label="Utenti totali" value={s.total_users} color="#22d3ee" testId="kpi-total-users" />
        <Kpi Icon={Activity} label="Attivi 24h" value={s.active_users} color="#34d399" testId="kpi-active-users" />
        <Kpi Icon={Crown} label="Abbonati" value={s.subscribers} color="#fbbf24" testId="kpi-subscribers" />
        <Kpi Icon={Hourglass} label="In prova" value={s.trial_users} color="#a78bfa" testId="kpi-trial" />
        <Kpi Icon={Timer} label="Minuti totali" value={s.total_minutes} color="#22d3ee" testId="kpi-minutes" />
        <Kpi Icon={Ban} label="Disabilitati" value={s.disabled_users} color="#fb7185" testId="kpi-disabled" />
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="hud-card p-5 lg:col-span-2">
          <p className="hud-tag mb-3">Andamento 14 giorni · richieste & minuti</p>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={s.trend}>
                <defs>
                  <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#22d3ee" stopOpacity={0.5} /><stop offset="1" stopColor="#22d3ee" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gm" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#a78bfa" stopOpacity={0.5} /><stop offset="1" stopColor="#a78bfa" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(148,163,184,.08)" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip {...tip} />
                <Area type="monotone" dataKey="requests" name="Richieste" stroke="#22d3ee" fill="url(#gr)" strokeWidth={2} />
                <Area type="monotone" dataKey="minutes" name="Minuti" stroke="#a78bfa" fill="url(#gm)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="hud-card p-5">
          <p className="hud-tag mb-3">Modelli più usati</p>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={s.models.map((m) => ({ ...m, name: NAMES[m.model] || m.model }))} layout="vertical">
                <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={70} />
                <Tooltip {...tip} />
                <Bar dataKey="count" name="Richieste" fill="#34d399" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="grid gap-5">
        <div className="hud-card p-5">
          <p className="hud-tag mb-3">Classifica paesi · la posizione di ogni utente è nel tab Utenti (link Google Maps)</p>
          <div className="space-y-2" data-testid="admin-country-ranking">
            {s.countries.map((c, i) => (
              <div key={c.country} className="flex items-center gap-3 text-sm">
                <span className="font-mono text-amber-300 w-5">{i + 1}</span>
                <span className="flex-1 text-slate-200 truncate">{c.country}</span>
                <span className="font-mono text-xs text-slate-400">{c.users} ut · {c.minutes} min</span>
              </div>
            ))}
          </div>
          <p className="hud-tag mt-6 mb-3">Top minuti per utente</p>
          <div className="space-y-1.5" data-testid="admin-top-minutes">
            {s.top_minutes.map((u) => (
              <div key={u.id} className="flex justify-between text-sm"><span className="truncate text-slate-300">{u.name}</span><span className="font-mono text-cyan-300">{u.minutes} min</span></div>
            ))}
          </div>
        </div>
      </div>
      <div className="hud-card p-5">
        <p className="hud-tag mb-3">Richieste per tipo</p>
        <div className="flex flex-wrap gap-3">
          {s.kinds.map((k) => <span key={k.type} className="rounded-full border border-cyan-500/25 px-4 py-1.5 text-sm font-mono">{k.type.toUpperCase()} · <span className="text-amber-300">{k.count}</span></span>)}
          {s.kinds.length === 0 && <span className="text-sm text-slate-500">Nessuna richiesta ancora.</span>}
        </div>
      </div>
    </div>
  );
};
